import React, { useEffect, useMemo, useState } from 'react';

import api from '../api';
import {
  GOALS_TEXT_SCOPE_STYLES,
  GOAL_TYPE_OPTIONS,
  MODULE_OPTIONS,
  REVIEW_FREQUENCY_OPTIONS,
  getModuleFieldConfigs,
  getModuleMeta,
  prettyLabel,
} from './shared';

const baseInputStyle = {
  padding: 12,
  borderRadius: 14,
  border: '1px solid #cbd5e1',
  width: '100%',
  background: '#fff',
};

function RenderDynamicField({ config, value, onChange }) {
  if (config.type === 'boolean') {
    return (
      <label style={{ display: 'flex', alignItems: 'center', gap: 10, border: '1px solid #cbd5e1', borderRadius: 14, padding: 14, background: '#fff', minHeight: 50 }}>
        <input type="checkbox" checked={Boolean(value)} onChange={(event) => onChange(event.target.checked)} />
        <span style={{ fontSize: 14, fontWeight: 600 }}>{config.checkboxLabel || `Enable ${config.label.toLowerCase()}`}</span>
      </label>
    );
  }

  if (config.type === 'multi-select') {
    const selectedValues = Array.isArray(value) ? value : [];
    return (
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
        {(config.options || []).map((option) => {
          const active = selectedValues.includes(option);
          return (
            <button
              key={option}
              type="button"
              onClick={() => onChange(active ? selectedValues.filter((item) => item !== option) : [...selectedValues, option])}
              style={{
                border: `1px solid ${active ? '#2563eb' : '#cbd5e1'}`,
                background: active ? '#dbeafe' : '#fff',
                color: active ? '#1d4ed8' : '#0f172a',
                borderRadius: 999,
                padding: '8px 12px',
                fontWeight: 700,
                fontSize: 12,
                cursor: 'pointer',
              }}
            >
              {prettyLabel(option)}
            </button>
          );
        })}
      </div>
    );
  }

  if (config.type === 'select') {
    return (
      <select value={value || ''} onChange={(event) => onChange(event.target.value)} style={baseInputStyle}>
        <option value="">Select {config.label.toLowerCase()}</option>
        {(config.options || []).map((option) => <option key={option} value={option}>{prettyLabel(option)}</option>)}
      </select>
    );
  }

  if (config.type === 'text') {
    return <textarea rows="3" value={value || ''} onChange={(event) => onChange(event.target.value)} placeholder={`Enter ${config.label.toLowerCase()}`} style={{ ...baseInputStyle, resize: 'vertical' }} />;
  }

  return (
    <input
      type="number"
      value={value ?? ''}
      onChange={(event) => onChange(event.target.value)}
      min={config.min}
      max={config.max}
      step={config.step || 1}
      placeholder="0"
      style={baseInputStyle}
    />
  );
}

export default function CreateGoalModal({ open, onClose, onCreated }) {
  const [step, setStep] = useState(1);
  const [users, setUsers] = useState([]);
  const [moduleFieldsConfig, setModuleFieldsConfig] = useState([]);
  const [moduleSpecificInputs, setModuleSpecificInputs] = useState({});
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    title: '',
    description: '',
    goal_type: 'REVENUE',
    target_value: '',
    target_unit: 'INR',
    assigned_module: '',
    assigned_to_user_id: '',
    priority: 3,
    start_date: '',
    end_date: '',
    review_frequency: 'MONTHLY',
  });

  const moduleMeta = getModuleMeta(form.assigned_module || 'ALL_MODULES');
  const shouldShowModuleStep = form.assigned_module && form.assigned_module !== 'ALL_MODULES';
  const maxStep = shouldShowModuleStep ? 3 : 2;

  const resetForm = () => {
    setStep(1);
    setUsers([]);
    setModuleFieldsConfig([]);
    setModuleSpecificInputs({});
    setError('');
    setSaving(false);
    setForm({
      title: '',
      description: '',
      goal_type: 'REVENUE',
      target_value: '',
      target_unit: 'INR',
      assigned_module: '',
      assigned_to_user_id: '',
      priority: 3,
      start_date: '',
      end_date: '',
      review_frequency: 'MONTHLY',
    });
  };

  useEffect(() => {
    if (!open) return;
    if (!form.assigned_module || form.assigned_module === 'ALL_MODULES') {
      setUsers([]);
      return;
    }
    const loadUsers = async () => {
      try {
        const response = await api.get('/goals/meta/users', { params: { module: form.assigned_module } });
        setUsers(response.data?.data || []);
      } catch {
        setUsers([]);
      }
    };
    loadUsers();
  }, [open, form.assigned_module]);

  useEffect(() => {
    if (!open) return;
    if (!form.assigned_module || form.assigned_module === 'ALL_MODULES') {
      setModuleFieldsConfig([]);
      return;
    }
    const loadModuleFields = async () => {
      try {
        const response = await api.get(`/goals/module-fields/${form.assigned_module}`);
        setModuleFieldsConfig(response.data?.fields || getModuleFieldConfigs(form.assigned_module));
      } catch {
        setModuleFieldsConfig(getModuleFieldConfigs(form.assigned_module));
      }
    };
    loadModuleFields();
  }, [open, form.assigned_module]);

  const stepLabels = useMemo(() => {
    const labels = ['Basic Details', 'Module Assignment'];
    if (shouldShowModuleStep) labels.push(`${prettyLabel(form.assigned_module)} Details`);
    return labels;
  }, [form.assigned_module, shouldShowModuleStep]);

  if (!open) return null;

  const updateForm = (next) => setForm((current) => ({ ...current, ...next }));

  const validateStep1 = () => {
    if (!form.title.trim()) return 'Goal title is required.';
    if (!form.target_value || Number(form.target_value) <= 0) return 'Target value must be greater than 0.';
    if (!form.start_date || !form.end_date) return 'Start and end dates are required.';
    if (new Date(form.end_date) < new Date(form.start_date)) return 'End date must be after start date.';
    return '';
  };

  const validateStep2 = () => {
    if (!form.assigned_module) return 'Please select a target module.';
    return '';
  };

  const validateStep3 = () => {
    for (const field of moduleFieldsConfig) {
      if (!field.required) continue;
      const value = moduleSpecificInputs[field.name];
      if (field.type === 'multi-select' && (!Array.isArray(value) || !value.length)) return `${field.label} is required.`;
      if (field.type !== 'boolean' && (value == null || value === '')) return `${field.label} is required.`;
    }
    return '';
  };

  const handleNext = () => {
    const validationMessage = step === 1 ? validateStep1() : validateStep2();
    if (validationMessage) {
      setError(validationMessage);
      return;
    }
    setError('');
    setStep((current) => Math.min(maxStep, current + 1));
  };

  const handleBack = () => {
    setError('');
    setStep((current) => Math.max(1, current - 1));
  };

  const submit = async () => {
    const validationMessage = shouldShowModuleStep ? validateStep3() : '';
    if (validationMessage) {
      setError(validationMessage);
      return;
    }

    setSaving(true);
    setError('');
    try {
      await api.post('/goals', {
        ...form,
        target_value: Number(form.target_value),
        assigned_to_user_id: form.assigned_to_user_id || null,
        module_fields_config: shouldShowModuleStep ? moduleSpecificInputs : {},
      });
      onCreated?.();
      onClose?.();
      resetForm();
    } catch (requestError) {
      setError(requestError?.response?.data?.error || 'Failed to create goal.');
    } finally {
      setSaving(false);
    }
  };

  const priorityTone = form.priority >= 4
    ? { bg: '#fee2e2', text: '#991b1b', label: form.priority === 5 ? 'Urgent' : 'High' }
    : form.priority === 3
      ? { bg: '#fef3c7', text: '#92400e', label: 'Medium' }
      : { bg: '#dcfce7', text: '#166534', label: 'Low' };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000, padding: 18 }}>
      <div className="goals-text-scope" style={{ width: '100%', maxWidth: 980, maxHeight: '92vh', overflowY: 'auto', background: '#fff', borderRadius: 28, boxShadow: '0 30px 80px rgba(15,23,42,0.28)', border: '1px solid #dbe5f1' }}>
        <style>{GOALS_TEXT_SCOPE_STYLES}</style>

        <div style={{ padding: 24, borderBottom: '1px solid #e2e8f0', background: 'linear-gradient(135deg,#f8fbff 0%,#eff6ff 54%,#ffffff 100%)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, alignItems: 'flex-start', flexWrap: 'wrap' }}>
            <div>
              <div style={{ fontSize: 13, fontWeight: 900, letterSpacing: '0.08em', color: '#1d4ed8' }}>GOAL CREATION WIZARD</div>
              <div style={{ marginTop: 8, fontSize: 30, fontWeight: 900, color: '#0f172a' }}>Create module-aware goals with the right inputs</div>
              <div style={{ marginTop: 10, fontSize: 14, lineHeight: 1.7, color: '#334155', maxWidth: 760 }}>
                Common details come first, then module assignment, then only the fields that matter for that department.
              </div>
            </div>
            <button type="button" onClick={() => { resetForm(); onClose?.(); }} style={{ border: '1px solid #cbd5e1', background: '#fff', borderRadius: 12, padding: '10px 14px', fontWeight: 800, cursor: 'pointer' }}>
              Close
            </button>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 22 }}>
            {stepLabels.map((label, index) => {
              const stepNumber = index + 1;
              const active = step === stepNumber;
              const done = step > stepNumber;
              return (
                <React.Fragment key={label}>
                  <div style={{ display: 'grid', justifyItems: 'center', gap: 6 }}>
                    <div style={{
                      width: 36,
                      height: 36,
                      borderRadius: '50%',
                      display: 'grid',
                      placeItems: 'center',
                      fontWeight: 900,
                      background: active ? '#2563eb' : done ? '#10b981' : '#e2e8f0',
                      color: active || done ? '#fff' : '#475569',
                    }}
                    >
                      {done ? '✓' : stepNumber}
                    </div>
                    <div style={{ fontSize: 12, fontWeight: active ? 800 : 600, color: active ? '#1d4ed8' : '#64748b' }}>{label}</div>
                  </div>
                  {index < stepLabels.length - 1 ? <div style={{ flex: 1, height: 2, background: done ? '#10b981' : '#dbe5f1', marginTop: -20 }} /> : null}
                </React.Fragment>
              );
            })}
          </div>
        </div>

        <div style={{ padding: 24 }}>
          {error ? (
            <div style={{ marginBottom: 18, padding: 14, borderRadius: 14, background: '#fee2e2', border: '1px solid #fca5a5', color: '#991b1b', fontWeight: 700 }}>
              {error}
            </div>
          ) : null}

          {step === 1 ? (
            <div style={{ display: 'grid', gap: 18 }}>
              <div style={{ border: '1px solid #dbe5f1', borderRadius: 20, background: '#f8fbff', padding: 20 }}>
                <div style={{ fontSize: 20, fontWeight: 900, color: '#0f172a' }}>Step 1: Basic Goal Details</div>
                <div style={{ marginTop: 6, fontSize: 14, color: '#475569' }}>Define what success looks like before deciding who should own it.</div>
              </div>

              <label style={{ display: 'grid', gap: 8, fontWeight: 700 }}>
                Goal Title *
                <input value={form.title} onChange={(event) => updateForm({ title: event.target.value })} placeholder="Increase monthly production output" style={baseInputStyle} />
              </label>

              <label style={{ display: 'grid', gap: 8, fontWeight: 700 }}>
                Description
                <textarea rows="4" value={form.description} onChange={(event) => updateForm({ description: event.target.value })} placeholder="Add context, business purpose, or expected outcome." style={{ ...baseInputStyle, resize: 'vertical' }} />
              </label>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 14 }}>
                <label style={{ display: 'grid', gap: 8, fontWeight: 700 }}>
                  Goal Type *
                  <select value={form.goal_type} onChange={(event) => updateForm({ goal_type: event.target.value })} style={baseInputStyle}>
                    {GOAL_TYPE_OPTIONS.map((option) => <option key={option} value={option}>{prettyLabel(option)}</option>)}
                  </select>
                </label>
                <label style={{ display: 'grid', gap: 8, fontWeight: 700 }}>
                  Target Value *
                  <input type="number" min="0" step="0.01" value={form.target_value} onChange={(event) => updateForm({ target_value: event.target.value })} placeholder="0" style={baseInputStyle} />
                </label>
                <label style={{ display: 'grid', gap: 8, fontWeight: 700 }}>
                  Target Unit *
                  <input value={form.target_unit} onChange={(event) => updateForm({ target_unit: event.target.value })} placeholder="INR / pieces / %" style={baseInputStyle} />
                </label>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 14 }}>
                <label style={{ display: 'grid', gap: 8, fontWeight: 700 }}>
                  Start Date *
                  <input type="date" value={form.start_date} onChange={(event) => updateForm({ start_date: event.target.value })} style={baseInputStyle} />
                </label>
                <label style={{ display: 'grid', gap: 8, fontWeight: 700 }}>
                  End Date *
                  <input type="date" value={form.end_date} onChange={(event) => updateForm({ end_date: event.target.value })} style={baseInputStyle} />
                </label>
                <label style={{ display: 'grid', gap: 8, fontWeight: 700 }}>
                  Review Frequency
                  <select value={form.review_frequency} onChange={(event) => updateForm({ review_frequency: event.target.value })} style={baseInputStyle}>
                    {REVIEW_FREQUENCY_OPTIONS.map((option) => <option key={option} value={option}>{prettyLabel(option)}</option>)}
                  </select>
                </label>
              </div>

              <div style={{ border: '1px solid #dbe5f1', borderRadius: 18, padding: 18, background: '#fff' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, alignItems: 'center', flexWrap: 'wrap' }}>
                  <div>
                    <div style={{ fontWeight: 900 }}>Priority</div>
                    <div style={{ marginTop: 4, fontSize: 13, color: '#64748b' }}>Higher priority goals are easier to spot in the governance queue.</div>
                  </div>
                  <div style={{ padding: '8px 12px', borderRadius: 999, background: priorityTone.bg, color: priorityTone.text, fontWeight: 900 }}>
                    {priorityTone.label}
                  </div>
                </div>
                <input type="range" min="1" max="5" value={form.priority} onChange={(event) => updateForm({ priority: Number(event.target.value) })} style={{ width: '100%', marginTop: 14 }} />
              </div>
            </div>
          ) : null}

          {step === 2 ? (
            <div style={{ display: 'grid', gap: 18 }}>
              <div style={{ border: `1px solid ${moduleMeta.border}`, borderRadius: 20, background: moduleMeta.accent, padding: 20 }}>
                <div style={{ fontSize: 20, fontWeight: 900, color: '#0f172a' }}>Step 2: Module Assignment</div>
                <div style={{ marginTop: 6, fontSize: 14, color: '#334155' }}>Pick the target department first so we can show the right users and fields.</div>
              </div>

              <div style={{ display: 'grid', gap: 10 }}>
                <div style={{ fontWeight: 800 }}>Select Module *</div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12 }}>
                  {MODULE_OPTIONS.map((moduleName) => {
                    const selected = form.assigned_module === moduleName;
                    const meta = getModuleMeta(moduleName);
                    return (
                      <button
                        key={moduleName}
                        type="button"
                        onClick={() => {
                          updateForm({ assigned_module: moduleName, assigned_to_user_id: '' });
                          setModuleSpecificInputs({});
                          setError('');
                        }}
                        style={{
                          border: `2px solid ${selected ? meta.color : '#dbe5f1'}`,
                          background: selected ? meta.accent : '#fff',
                          borderRadius: 18,
                          padding: 16,
                          textAlign: 'left',
                          cursor: 'pointer',
                          boxShadow: selected ? `0 16px 28px ${meta.color}22` : 'none',
                        }}
                      >
                        <div style={{ fontSize: 28 }}>{meta.icon}</div>
                        <div style={{ marginTop: 8, fontWeight: 900, color: '#0f172a' }}>{prettyLabel(moduleName)}</div>
                        <div style={{ marginTop: 6, fontSize: 12, lineHeight: 1.5, color: '#475569' }}>{meta.description}</div>
                      </button>
                    );
                  })}
                </div>
              </div>

              {form.assigned_module ? (
                <div style={{ border: '1px solid #dbe5f1', borderRadius: 18, background: '#fff', padding: 18, display: 'grid', gap: 12 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
                    <div>
                      <div style={{ fontWeight: 900 }}>Selected Module: {prettyLabel(form.assigned_module)}</div>
                      <div style={{ marginTop: 4, fontSize: 13, color: '#64748b' }}>{moduleMeta.description}</div>
                    </div>
                    <div style={{ padding: '8px 12px', borderRadius: 999, background: moduleMeta.accent, color: moduleMeta.color, fontWeight: 900, border: `1px solid ${moduleMeta.border}` }}>
                      {moduleMeta.icon} {prettyLabel(form.assigned_module)}
                    </div>
                  </div>

                  {form.assigned_module !== 'ALL_MODULES' ? (
                    <label style={{ display: 'grid', gap: 8, fontWeight: 700 }}>
                      Assign to User (Optional)
                      <select value={form.assigned_to_user_id} onChange={(event) => updateForm({ assigned_to_user_id: event.target.value })} style={baseInputStyle}>
                        <option value="">Unassigned</option>
                        {users.map((user) => <option key={user.id} value={user.id}>{user.username || user.email || user.id}</option>)}
                      </select>
                      <span style={{ fontSize: 12, color: '#64748b' }}>
                        {users.length ? `Showing users mapped to ${prettyLabel(form.assigned_module)}.` : `No users found for ${prettyLabel(form.assigned_module)} yet.`}
                      </span>
                    </label>
                  ) : (
                    <div style={{ padding: 14, borderRadius: 14, background: '#f8fafc', border: '1px dashed #cbd5e1', fontSize: 14, color: '#475569' }}>
                      Company-wide goals skip module-specific inputs and are visible in every module workspace.
                    </div>
                  )}
                </div>
              ) : null}
            </div>
          ) : null}

          {step === 3 ? (
            <div style={{ display: 'grid', gap: 18 }}>
              <div style={{ border: `1px solid ${moduleMeta.border}`, borderRadius: 20, background: moduleMeta.accent, padding: 20 }}>
                <div style={{ fontSize: 20, fontWeight: 900, color: '#0f172a' }}>Step 3: {prettyLabel(form.assigned_module)}-specific details</div>
                <div style={{ marginTop: 6, fontSize: 14, color: '#334155' }}>Capture the KPIs and context this module actually cares about.</div>
              </div>

              {moduleFieldsConfig.length ? (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 16 }}>
                  {moduleFieldsConfig.map((field) => (
                    <div key={field.name} style={{ border: '1px solid #dbe5f1', borderRadius: 18, background: '#fff', padding: 16, display: 'grid', gap: 10 }}>
                      <div>
                        <div style={{ fontWeight: 900 }}>
                          {field.label} {field.required ? <span style={{ color: '#dc2626' }}>*</span> : null}
                        </div>
                        {field.helpText ? <div style={{ marginTop: 4, fontSize: 12, color: '#64748b' }}>{field.helpText}</div> : null}
                      </div>
                      <RenderDynamicField
                        config={field}
                        value={moduleSpecificInputs[field.name]}
                        onChange={(nextValue) => setModuleSpecificInputs((current) => ({ ...current, [field.name]: nextValue }))}
                      />
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{ border: '1px dashed #cbd5e1', borderRadius: 18, background: '#f8fafc', padding: 20, fontSize: 14, color: '#475569' }}>
                  No extra fields are configured for this module. You can create the goal immediately.
                </div>
              )}
            </div>
          ) : null}
        </div>

        <div style={{ padding: 24, borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
          <button type="button" onClick={handleBack} disabled={step === 1} style={{ border: '1px solid #cbd5e1', background: '#fff', borderRadius: 12, padding: '12px 18px', fontWeight: 800, cursor: step === 1 ? 'not-allowed' : 'pointer', opacity: step === 1 ? 0.5 : 1 }}>
            Back
          </button>

          {step < maxStep ? (
            <button type="button" onClick={handleNext} style={{ border: 0, background: '#2563eb', color: '#fff', borderRadius: 12, padding: '12px 18px', fontWeight: 800, cursor: 'pointer' }}>
              Next
            </button>
          ) : (
            <button type="button" onClick={submit} disabled={saving} style={{ border: 0, background: saving ? '#94a3b8' : '#10b981', color: '#fff', borderRadius: 12, padding: '12px 18px', fontWeight: 800, cursor: saving ? 'not-allowed' : 'pointer' }}>
              {saving ? 'Creating...' : 'Create Goal'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
